import { parseLrc, pickSyncedHit, timedLyricsFromHit, titleQueries } from './lrcModel';
/**
 * LyricsService - Multi-source lyrics fetcher with fallbacks
 * Uses multiple free APIs for better coverage
 */

/**
 * Fetch lyrics from lyrics.ovh API
 */
const fetchFromLyricsOvh = async (title, artist) => {
	try {
		const safeArtist = artist || 'Unknown';
		const url = `https://api.lyrics.ovh/v1/${encodeURIComponent(safeArtist)}/${encodeURIComponent(title)}`;
		const res = await fetch(url);
		if (!res.ok) return null;
		const data = await res.json();
		if (!data || !data.lyrics) return null;
		return {
			text: data.lyrics,
			source: 'lyrics.ovh',
		};
	} catch (err) {
		console.error('lyrics.ovh fetch failed', err);
		return null;
	}
};

/**
 * Fetch lyrics from api-ninjas.com Lyrics API
 * Free tier: 50,000 requests/month (no auth needed for CORS)
 */
const fetchFromApiNinjas = async (title, artist) => {
	try {
		const params = new URLSearchParams();
		params.append('title', title);
		if (artist) params.append('artist', artist);

		const url = `https://api.api-ninjas.com/v1/lyrics?${params.toString()}`;
		const res = await fetch(url, {
			headers: {
				'X-Api-Key': 'DEMO_KEY', // Replace with your API key for production
			},
		});

		if (!res.ok) return null;
		const data = await res.json();
		if (!data || !data.lyrics) return null;

		return {
			text: data.lyrics,
			source: 'API Ninjas',
		};
	} catch (err) {
		console.error('API Ninjas fetch failed', err);
		return null;
	}
};

/**
 * Fallback: try to fetch from LRCLIB (free, no auth, good coverage)
 */
const fetchFromLrclib = async (title, artist) => {
	try {
		console.log('  [LRCLIB] Function entered, building URL...');
		const params = new URLSearchParams();
		params.append('track_name', title);
		if (artist) params.append('artist_name', artist);

		const url = `https://lrclib.net/api/search?${params.toString()}`;
		console.log('  [LRCLIB] Fetching from:', url);
		const res = await fetch(url);

		if (!res.ok) return null;
		const data = await res.json();

		if (!data || !Array.isArray(data) || data.length === 0) return null;

		// Get the first match
		const match = data[0];
		if (!match.plainLyrics && !match.syncedLyrics) return null;

		// A hit with only synced lyrics used to store its raw "[00:12.34]"
		// stamps as the text (2026-10-02): strip them to the words.
		const text = match.plainLyrics || parseLrc(match.syncedLyrics).map((l) => l.text).join('\n');
		if (!text) return null;
		return { text, source: 'LRCLIB' };
	} catch (err) {
		console.error('LRCLIB fetch failed', err);
		return null;
	}
};

/**
 * Common misspellings and corrections for song/artist names
 */
const COMMON_CORRECTIONS = {
	// Common typos
	'sledghammer': 'sledgehammer',
	'bohemain': 'bohemian',
	'rapsody': 'rhapsody',
	'rhapsody': 'rhapsody',
	'tommorow': 'tomorrow',
	'tommorrow': 'tomorrow',
	'beautful': 'beautiful',
	'beatiful': 'beautiful',
	'definately': 'definitely',
	'seperate': 'separate',
	'occured': 'occurred',
	'recieve': 'receive',
	'beleive': 'believe',
	'wierd': 'weird',
	'freind': 'friend',
	'untill': 'until',
	'beggining': 'beginning',
	'begining': 'beginning',
	'occassion': 'occasion',
	'neccessary': 'necessary',
	'accomodate': 'accommodate',
	'priviledge': 'privilege',
	'occurence': 'occurrence',
	// Musical terms
	'dont': "don't",
	'cant': "can't",
	'wont': "won't",
	'im': "i'm",
	'ive': "i've",
	'youre': "you're",
	'theyre': "they're",
	'wouldnt': "wouldn't",
	'couldnt': "couldn't",
	'shouldnt': "shouldn't",
	// Common artist misspellings
	'beyonce': 'beyoncé',
	'adelle': 'adele',
	'arianna': 'ariana',
	'led zepplin': 'led zeppelin',
	'metalica': 'metallica',
	'nikelback': 'nickelback',
};

/**
 * Fix common misspellings in a string
 */
const fixCommonMisspellings = (text) => {
	if (!text) return text;

	let fixed = text.toLowerCase();

	// Check each word against corrections
	for (const [wrong, right] of Object.entries(COMMON_CORRECTIONS)) {
		const regex = new RegExp(`\\b${wrong}\\b`, 'gi');
		fixed = fixed.replace(regex, right);
	}

	// Restore original casing for first letter of each word
	const words = text.split(' ');
	const fixedWords = fixed.split(' ');

	return fixedWords.map((word, i) => {
		if (words[i] && words[i][0] === words[i][0].toUpperCase()) {
			return word.charAt(0).toUpperCase() + word.slice(1);
		}
		return word;
	}).join(' ');
};

/**
 * Generate search variations for fuzzy matching
 * Returns array of { title, artist } objects to try
 */
const generateSearchVariations = (title, artist) => {
	const variations = [];

	// 1. Original (cleaned)
	variations.push({ title, artist });

	// 2. With misspelling corrections
	const correctedTitle = fixCommonMisspellings(title);
	const correctedArtist = fixCommonMisspellings(artist);
	if (correctedTitle !== title || correctedArtist !== artist) {
		variations.push({ title: correctedTitle, artist: correctedArtist });
	}

	// 3. Title only (no artist) - sometimes artist name causes mismatches
	if (artist) {
		variations.push({ title, artist: '' });
		if (correctedTitle !== title) {
			variations.push({ title: correctedTitle, artist: '' });
		}
	}

	// 4. Remove common prefixes/suffixes that might cause issues
	const simplifiedTitle = title
		.replace(/^(the|a|an)\s+/i, '') // Remove leading articles
		.replace(/\s*\(.*?\)\s*/g, '') // Remove any parenthetical content
		.replace(/\s*\[.*?\]\s*/g, '') // Remove bracketed content
		.replace(/['']/g, "'") // Normalize apostrophes
		.trim();

	if (simplifiedTitle !== title && simplifiedTitle.length > 2) {
		variations.push({ title: simplifiedTitle, artist });
		variations.push({ title: simplifiedTitle, artist: '' });
	}

	// Remove duplicates
	const seen = new Set();
	return variations.filter(v => {
		const key = `${v.title}|${v.artist}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

/**
 * Clean up title and artist for better lyrics API matching
 * Removes extra details that confuse lyrics databases
 */
const cleanForLyricsSearch = (title, artist) => {
	let cleanTitle = title;
	let cleanArtist = artist;

	// Remove common metadata noise from titles
	// e.g., "Tonight (from "West Side Story" Original Broadway Cast)" → "Tonight"
	cleanTitle = cleanTitle
		.replace(/\s*\(from\s+["'].*?["']\s*.*?\)/gi, '') // Remove "(from "Show Name" ...)"
		.replace(/\s*\(.*?cast.*?\)/gi, '') // Remove "(Original Broadway Cast)"
		.replace(/\s*\(.*?soundtrack.*?\)/gi, '') // Remove "(Soundtrack)"
		.replace(/\s*-\s*(original|broadway|cast|soundtrack).*$/gi, '') // Remove "- Original Cast Recording"
		.trim();

	// Simplify artist names
	// e.g., "Carol Lawrence, Larry Kert, Max Goberman & Orchestra" → "Carol Lawrence"
	// or "'Suffs' Original Broadway Cast" → "Suffs Cast"
	cleanArtist = cleanArtist
		.replace(/^["'](.+?)["']\s*/g, '$1 ') // Remove leading quotes: "'Suffs'" → "Suffs"
		.replace(/\s*original broadway cast\s*/gi, ' Cast') // Simplify cast references
		.replace(/\s*&\s*.*/gi, '') // Remove "& other artists"
		.replace(/\s*,\s*.*/gi, '') // Remove ", other artists" (keep first artist only)
		.replace(/\s+orchestra$/gi, '') // Remove "Orchestra"
		.replace(/\s+ensemble$/gi, '') // Remove "Ensemble"
		.trim();

	console.log(`  📝 Cleaned: "${title}" → "${cleanTitle}" | "${artist}" → "${cleanArtist}"`);

	return { title: cleanTitle, artist: cleanArtist };
};

/**
 * Try fetching lyrics from all sources for a given title/artist pair
 */
const tryAllSources = async (title, artist) => {
	// Try LRCLIB first (best free coverage, no auth needed)
	let result = await fetchFromLrclib(title, artist);
	if (result) return result;

	// Try lyrics.ovh second
	result = await fetchFromLyricsOvh(title, artist);
	if (result) return result;

	// Try API Ninjas as last resort
	result = await fetchFromApiNinjas(title, artist);
	if (result) return result;

	return null;
};

/**
 * Main lyrics fetcher with multiple fallbacks and fuzzy matching
 * Tries multiple sources and search variations until one succeeds
 */
export const fetchLyrics = async (title, artist = '') => {
	if (!title) return null;

	console.log(`Fetching lyrics for: "${title}" by "${artist || 'Unknown'}"`);

	// Clean up title and artist for better matching
	const { title: cleanTitle, artist: cleanArtist } = cleanForLyricsSearch(title, artist);

	// Generate search variations (original, corrected spelling, simplified, etc.)
	const variations = generateSearchVariations(cleanTitle, cleanArtist);
	console.log(`  🔍 Generated ${variations.length} search variation(s)`);

	// Try each variation until we find lyrics
	for (let i = 0; i < variations.length; i++) {
		const { title: varTitle, artist: varArtist } = variations[i];

		if (i === 0) {
			console.log(`→ Trying: "${varTitle}" by "${varArtist || '(no artist)'}"`);
		} else {
			console.log(`→ Retry ${i}: "${varTitle}" by "${varArtist || '(no artist)'}"`);
		}

		const result = await tryAllSources(varTitle, varArtist);
		if (result) {
			console.log(`✓ Found lyrics from ${result.source}`);
			return result;
		}
	}

	console.log('✗ No lyrics found from any source after trying all variations');
	return null;
};

/**
 * Get lyrics source providers info
 */
export const getLyricsProviders = () => [
	{
		name: 'LRCLIB',
		url: 'https://lrclib.net',
		requiresAuth: false,
		description: 'Best free coverage, community-maintained',
	},
	{
		name: 'lyrics.ovh',
		url: 'https://lyrics.ovh',
		requiresAuth: false,
		description: 'Basic free API, limited coverage',
	},
	{
		name: 'API Ninjas',
		url: 'https://api-ninjas.com',
		requiresAuth: true,
		description: 'Good coverage, requires API key',
	},
];

export default {
	fetchLyrics,
	getLyricsProviders,
};

/**
 * Words WITH their timing, for a song just added (Lesson Dashboard pass 4 S1,
 * 2026-10-02): lrclib's synced lines for the same title (and artist, when
 * known), so a lyric video can be followed before any track exists. Falls
 * back to plain words from every source (fetchLyrics), which come back with
 * no `timed`. Returns null when nothing is found anywhere.
 */
export const fetchTimedLyrics = async (title, artist = '', { exclude = [], again = false } = {}) => {
	if (!title) return null;
	// "Wrong lyrics → Search again" passes the lrclib ids already shown: the
	// next synced match, or nothing. It never falls back to the plain search,
	// whose first hit is very likely the same wrong song again.

	for (const t of titleQueries(title)) {
		try {
			const params = new URLSearchParams({ track_name: t });
			if (artist) params.append('artist_name', artist);
			const res = await fetch(`https://lrclib.net/api/search?${params.toString()}`);
			if (!res.ok) continue;
			const hit = pickSyncedHit(await res.json(), { title, artist, exclude });
			if (hit) return timedLyricsFromHit(hit);
		} catch (err) {
			console.error('lrclib timed search failed', err);
			if (again) throw err;
		}
	}
	return again ? null : fetchLyrics(title, artist);
};
